import axios from 'axios';
import toast from 'react-hot-toast';
import axiosClient from '../lib/http/axiosClient';

// Enhanced parameter validation
const validateAndSanitizeParams = (params) => {
    if (!params || typeof params !== 'object') {
        return null;
    }
    
    const sanitized = {};
    let hasValidParams = false;
    
    for (const [key, value] of Object.entries(params)) {
        // Skip null, undefined, empty strings, and empty objects
        if (value === null || value === undefined || value === '') {
            continue;
        }
        
        // Handle nested objects
        if (typeof value === 'object' && !Array.isArray(value)) {
            const nestedSanitized = validateAndSanitizeParams(value);
            if (nestedSanitized && Object.keys(nestedSanitized).length > 0) {
                sanitized[key] = nestedSanitized;
                hasValidParams = true;
            }
            continue;
        }
        
        // Handle arrays
        if (Array.isArray(value)) {
            const filteredArray = value.filter(item => 
                item !== null && item !== undefined && item !== ''
            );
            if (filteredArray.length > 0) {
                sanitized[key] = filteredArray;
                hasValidParams = true;
            }
            continue;
        }
        
        // Handle primitive values
        sanitized[key] = value;
        hasValidParams = true;
    }
    
    return hasValidParams ? sanitized : null;
};

// Base API client configuration
const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '/api/v1',
    timeout: 120000,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Request interceptor for authentication and parameter validation
apiClient.interceptors.request.use(
    (config) => {
        console.log('🌐 API CLIENT: Making request');
        console.log('📤 Request config:', {
            method: config.method?.toUpperCase(),
            url: config.url,
            baseURL: config.baseURL,
            fullURL: `${config.baseURL}${config.url}`,
            headers: config.headers,
            data: config.data,
            params: config.params
        });
        
        // Read from localStorage using the same key as the legacy system
        let loginData = null;
        try {
            const storageData = localStorage.getItem('loginSuccessNeurulaHealth');
            if (storageData) {
                loginData = JSON.parse(storageData);
            }
        } catch (error) {
            console.error("Error parsing login data from localStorage:", error);
            loginData = null;
        }
        
        if (loginData && loginData.authToken) {
            config.headers.Authorization = `Bearer ${loginData.authToken}`;
            console.log('🔐 Authentication token added to request');
        } else {
            console.warn('⚠️ No authentication token found');
        }
        
        // Validate and sanitize parameters for GET requests
        if (config.method?.toLowerCase() === 'get' && config.params) {
            const sanitizedParams = validateAndSanitizeParams(config.params);
            if (sanitizedParams) {
                config.params = sanitizedParams;
            } else {
                // Remove params if no valid parameters after sanitization
                delete config.params;
            }
        }
        
        // Add request timestamp for debugging
        config.metadata = { startTime: new Date() };
        
        console.log('✅ Request interceptor completed');
        return config;
    },
    (error) => {
        console.error('💥 Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Note: Error handling is now managed by the global axiosClient
// This keeps the existing logging functionality but removes duplicate error toasts
// Response interceptor for logging only
apiClient.interceptors.response.use(
    (response) => {
        // Calculate request duration
        const duration = new Date() - response.config.metadata.startTime;
        
        console.log('✅ API CLIENT: Response received');
        console.log('📥 Response details:', {
            method: response.config.method?.toUpperCase(),
            url: response.config.url,
            status: response.status,
            statusText: response.statusText,
            duration: `${duration}ms`,
            data: response.data,
            headers: response.headers
        });
        
        // Only log slow requests or errors to reduce console spam
        if (duration > 1000) {
            console.warn(`Slow API Call: ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
        }
        
        return response;
    },
    (error) => {
        console.error('💥 API CLIENT: Response error');
        console.error('❌ Error details:', {
            method: error.config?.method?.toUpperCase(),
            url: error.config?.url,
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.message,
            data: error.response?.data,
            headers: error.response?.headers
        });
        
        const status = error.response?.status;
        const message = error.response?.data?.detail || error.response?.data?.error?.message || error.message || 'An error occurred';
        const url = error.config?.url;
        
        // Log error for debugging
        console.error(`API Error: ${error.config?.method?.toUpperCase()} ${url}`, {
            status,
            message,
            data: error.response?.data
        });
        
        // Handle 401 redirect (still needed for this legacy client)
        if (status === 401) {
            localStorage.removeItem('authToken');
            window.location.href = '/login';
        }
        
        return Promise.reject(error);
    }
);

// Helper function to handle API responses consistently
export const handleApiResponse = (response) => {
    return {
        success: true,
        data: response.data,
        status: response.status,
        message: 'Operation completed successfully'
    };
};

// Helper function to handle API errors consistently
export const handleApiError = (error) => {
    return {
        success: false,
        data: null,
        status: error.response?.status || 0,
        message: error.response?.data?.detail || error.message || 'An error occurred'
    };
};

// Export the new global client for components that want to migrate
export { axiosClient as globalApiClient };

export default apiClient; 